import React, { useCallback, useEffect, useMemo } from "react";
import { Typography, Card, Spin } from "antd";
import { useFetchApi } from "../../../../../../hooks/useFetchApi";
import { flattenMetrics } from "../../../../../../helper";

const { Text } = Typography;

interface SectorIntelligenceProps {
  selectedStock?: any;
  baseCurrency: string;
  metricsData?: any;
}

const INCLUDED_METRIC_NAMES = [
  "Current Assets Ratio",
  "Return on Equity",
  "Quick Ratio",
];

const METRIC_CARDS = [
  {
    name: "Current Assets Ratio",
    label: "Current Ratio",
    color: "#3B82F6",
    suffix: "x",
  },
  {
    name: "Return on Equity",
    label: "Return on Equity",
    color: "#F97316",
    suffix: "%",
  },
  {
    name: "Quick Ratio",
    label: "Quick Asset Ratio",
    color: "#F59E0B",
    suffix: "x",
  },
];

export const SectorIntelligence: React.FC<SectorIntelligenceProps> = ({
  selectedStock,
  baseCurrency,
  metricsData,
}) => {
  const {
    execute: generateSectorGraph,
    loading: sectorLoading,
    response: sectorGraphData,
  } = useFetchApi("/api/dynamic-graph");

  const selectedMetrics = useMemo(() => {
    if (!metricsData) return [];
    const flattened = flattenMetrics(metricsData);
    return INCLUDED_METRIC_NAMES.map((name) =>
      flattened.find((m: any) => m.name?.trim() === name),
    ).filter(Boolean);
  }, [metricsData]);

  const fetchData = useCallback(() => {
    if (!selectedStock || selectedMetrics.length === 0) return;

    generateSectorGraph({
      selectedStocks: [selectedStock],
      filter: "annually",
      baseCurrency,
      selectedMetrics,
      period: "3",
    });
  }, [selectedStock, baseCurrency, selectedMetrics]);

  useEffect(() => {
    fetchData();
  }, [selectedStock, baseCurrency, selectedMetrics]);

  const values = useMemo(() => {
    const series = sectorGraphData?.series;

    if (!series || series.length < INCLUDED_METRIC_NAMES.length) {
      return null;
    }

    return series.map((s: any) => {
      const last = s.data?.[s.data.length - 1];
      return last ? parseFloat(last) : 0;
    });
  }, [sectorGraphData]);

  return (
    <Card className="vi-glass-card w-full shadow-sm rounded-xl">
      <div className="mb-4">
        <Text strong className="text-sm text-gray-800 block">
          Sector Intelligence
        </Text>
        <Text className="text-xs text-gray-400">
          Key efficiency and liquidity ratios from the latest annual report
        </Text>
      </div>

      {sectorLoading || !values ? (
        <div className="h-48 flex items-center justify-center">
          <Spin />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {METRIC_CARDS.map((card, i) => (
            <div
              key={card.name}
              className="relative border border-gray-200 rounded-lg p-4 pr-8"
            >
              <div
                className="absolute top-3 right-3 bottom-3 w-1 rounded-full"
                style={{ backgroundColor: card.color }}
              />
              <Text className="text-sm text-gray-800 font-medium block mb-1">
                {card.label}
              </Text>
              <Text className="text-2xl font-bold text-gray-900 block">
                {values[i] !== undefined
                  ? `${values[i].toFixed(2)}${card.suffix}`
                  : "-"}
              </Text>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
